import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import { cloneDeep, set } from 'lodash';
import { isValidHandleBarNameWithTrailingDot, isValidHeaderName } from 'utils';
import { replaceHandleBars } from 'utils/handlebarReplacement';
import type { IQueryParam, IRequestExplorerState, IRequestExplorerStateActions } from './RequestExplorerContext';
import { RequestExplorerActionTypes } from './RequestExplorerContext';

const recreateUrl = (state: IRequestExplorerState): IRequestExplorerState => {
  // The replacement to '%7D' '%7B' is in the construct
  /**
   * @todo make a better fix then this replacement
   */
  const urlObj = new URL(state.url);

  const usedParams = state.params.filter(param => param.use).map(param => [param.key, param.value]);
  urlObj.search = new URLSearchParams(usedParams).toString();
  const { href } = urlObj;

  const newUrl = href.replaceAll('%7D', '}').replaceAll('%7B', '{');
  state.url = newUrl;

  return state;
};

export const requestExplorerReducer = (
  draftState: IRequestExplorerState,
  action: IRequestExplorerStateActions
): IRequestExplorerState => {
  switch (action.type) {
    case RequestExplorerActionTypes.UPDATE_METHOD:
      return { ...draftState, method: action.payload };
    case RequestExplorerActionTypes.UPDATE_URL: {
      const params = extractUrlParams(action.payload);

      return { ...draftState, url: action.payload, params };
    }
    case RequestExplorerActionTypes.DELETE_PARAM: {
      // remove and re-index
      const newState = { ...draftState };
      newState.params = draftState.params.filter((value, i) => i !== action.payload.index);

      return recreateUrl(newState);
    }
    case RequestExplorerActionTypes.UPDATE_PARAM: {
      const newState = { ...draftState };

      const params = cloneDeep(draftState.params);
      if (!params[action.payload.index]) {
        params[action.payload.index] = {
          use: true,
          key: '',
          value: '',
          valid: true
        };
      }
      if (action.payload.key !== undefined) {
        params[action.payload.index].key = action.payload.key;
      } else if (action.payload.value !== undefined) {
        const oldValue = params[action.payload.index].value;
        const myOldBars: Array<{ key: string; value?: string; ref?: Array<Array<string>> }> = [];
        findHandleBars(oldValue, [], {}, myOldBars);
        const myNewBars: Array<{ key: string; value?: string; ref?: Array<Array<string>> }> = [];
        findHandleBars(action.payload.value, [], {}, myNewBars);
        // Todo?? update?
        params[action.payload.index].value = action.payload.value;
      }
      newState.params = params;

      return recreateUrl(newState);
    }
    case RequestExplorerActionTypes.TOGGLE_PARAM: {
      const newState = { ...draftState };
      const params = cloneDeep(draftState.params);
      params[action.payload.index].use = action.payload.active;
      newState.params = params;

      return recreateUrl(newState);
    }
    case RequestExplorerActionTypes.DELETE_HEADER: {
      // remove and re-index
      const newState = { ...draftState };
      newState.headers = draftState.headers.filter((value, i) => i !== action.payload.index);

      return recreateUrl(newState);
    }
    case RequestExplorerActionTypes.UPDATE_HEADER: {
      const newState = { ...draftState };

      if (action.payload.key && action.payload.key !== '' && !action.payload.key.match(isValidHeaderName)) {
        // eslint-disable-next-line no-console
        console.log('invalid header name');

        return newState;
      }

      const headers = cloneDeep(draftState.headers);
      if (!headers[action.payload.index]) {
        headers[action.payload.index] = {
          use: true,
          key: '',
          value: '',
          valid: true
        };
      }
      if (action.payload.key) {
        headers[action.payload.index].key = action.payload.key;
      } else if (action.payload.value) {
        headers[action.payload.index].value = action.payload.value;
      }
      newState.headers = headers;

      return recreateUrl(newState);
    }
    case RequestExplorerActionTypes.TOGGLE_HEADER: {
      const newState = { ...draftState };
      const headers = cloneDeep(draftState.headers);
      headers[action.payload.index].use = action.payload.active;
      newState.headers = headers;

      return recreateUrl(newState);
    }
    case RequestExplorerActionTypes.UPDATE_BODY: {
      return { ...draftState, body: action.payload };
    }
    case RequestExplorerActionTypes.ADD_LOG_LINE: {
      return { ...draftState, sessionLog: [...draftState.sessionLog, action.payload] };
    }
    case RequestExplorerActionTypes.SET_RESPONSE:
      return {
        ...draftState,
        response: {
          code: action.payload.resultCode,
          headers: action.payload.headers,
          body: action.payload.resultBody ? action.payload.resultBody : undefined
        }
      };
    case RequestExplorerActionTypes.DELETE_VARIABLE: {
      // remove and re-index
      const newState = { ...draftState };
      newState.variables = draftState.variables.filter((value, i) => i !== action.payload.index);

      return newState;
    }
    case RequestExplorerActionTypes.UPDATE_VARIABLE: {
      const newState = { ...draftState };
      if (
        action.payload.key &&
        action.payload.key !== '' &&
        !action.payload.key.match(isValidHandleBarNameWithTrailingDot)
      ) {
        // eslint-disable-next-line no-console
        console.log('invalid handlebar name');

        return newState;
      }

      const variables = cloneDeep(draftState.variables);
      if (!variables[action.payload.index]) {
        variables[action.payload.index] = {
          key: '',
          value: ''
        };
      }
      if (action.payload.key) {
        const oldKey = variables[action.payload.index].key;
        const newKey = action.payload.key;

        const isDuplicate = variables.find(variable => variable.key === newKey);
        variables[action.payload.index].key = action.payload.key;
        if (isDuplicate) {
          // only set when we don't have one
          if (variables[action.payload.index].orgKey === undefined) {
            // set the key to rename once we have a valid one
            variables[action.payload.index].orgKey = oldKey;
          }
        } else {
          const realOldKey = variables[action.payload.index].orgKey;
          delete variables[action.payload.index].orgKey;
          // if variables[action.payload.index].ref?.fi
          // rename in all
          const replaceData: IPropertyBag = {};
          set(replaceData, realOldKey || oldKey, `{${newKey}}`);
          replaceAllHandleBars(newState, replaceData);
          // newState.url = replaceHandleBars(newState.url, replaceData);
          // newState.body = replaceHandleBars(newState.body, replaceData);
          // headers
          // params
        }
      } else if (action.payload.value) {
        variables[action.payload.index].value = action.payload.value;
      }
      newState.variables = variables;

      return newState;
    }
    case RequestExplorerActionTypes.TOGGLE_VARIABLE: {
      const newState = { ...draftState };
      const variables = cloneDeep(draftState.variables);
      // headers[action.payload.index].use = action.payload.active;
      newState.variables = variables;

      return newState;
    }
    case RequestExplorerActionTypes.UPDATE_RESPONSE_STRUCTURE: {
      return { ...draftState, responseStructureUuid: action.payload.uuid, responseStructure: action.payload.structure };
    }
    default:
      return draftState;
  }
};

export const extractUrlParams = (url: string): Array<IQueryParam> => {
  const output: Array<IQueryParam> = [];
  try {
    const urlObj = new URL(url);

    urlObj.searchParams.forEach((value, key, parent) => {
      output.push({
        key,
        value,
        use: true,
        valid: true
      });
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('extractUrlParams', err);
  }

  return output;
};

export const extractParams = (url: string): Array<IQueryParam> => {
  const output: Array<IQueryParam> = [];
  try {
    const urlObj = new URL(url);

    urlObj.searchParams.forEach((value, key, parent) => {
      output.push({
        key,
        value,
        use: true,
        valid: true
      });
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('extractUrlParams', err);
  }

  return output;
};

const findHandleBars = (
  text: string | undefined,
  reference: Array<string>,
  data: IPropertyBag,
  allVariables: Array<{ key: string; value?: string; ref?: Array<Array<string>> }>
) => {
  if (!text) {
    return;
  }
  replaceHandleBars(text, data, {
    onHit(hit, value) {
      allVariables.push({ key: hit.slice(1, -1), value, ref: [reference] });
    },
    onHandleMiss(miss) {
      allVariables.push({ key: miss.slice(1, -1), ref: [reference] });

      return undefined;
    }
  });
};

const findAllHandleBars = (
  state: IRequestExplorerState,
  allData: IPropertyBag
): Array<{ key: string; value?: string }> => {
  const allHandleBars: Array<{ key: string; value?: string; ref?: Array<Array<string>> }> = [];
  findHandleBars(state.url, ['url'], allData, allHandleBars);
  findHandleBars(state.body, ['body'], allData, allHandleBars);
  state.headers.forEach(header => {
    findHandleBars(header.key, ['header', header.key, 'key'], allData, allHandleBars);
    findHandleBars(header.value, ['header', header.key, 'value'], allData, allHandleBars);
  });

  state.params.forEach(param => {
    if (!param.use) {
      // Else I would have already parsed in the url
      findHandleBars(param.key, ['param', param.key, 'key'], allData, allHandleBars);
      findHandleBars(param.value, ['param', param.key, 'value'], allData, allHandleBars);
    }
  });
  if (state.authentication) {
    mapRecord(state.authentication, (value, key) => {
      if (typeof value === 'string') {
        findHandleBars(value, ['authentication', key, 'value'], allData, allHandleBars);
      }
    });
  }

  const filtered: typeof allHandleBars = [];
  allHandleBars.forEach(item => {
    const found = filtered.find(searchItem => searchItem.key === item.key);
    if (!found) {
      filtered.push(item);
    } else {
      found.ref = [...(found.ref || []), ...(item.ref || [])];
    }
  });

  return filtered;
};

export const initRequestExplorerState = (state: IRequestExplorerState): IRequestExplorerState => {
  const allData: IPropertyBag = {};
  const newState = { ...state, variables: findAllHandleBars(state, allData) };

  return newState;
};

const replaceAllHandleBars = (state: IRequestExplorerState, replaceData: IPropertyBag): IRequestExplorerState => {
  const allHandleBars: Array<{ key: string; value?: string; ref?: Array<Array<string>> }> = [];
  state.url = replaceHandleBars(state.url, replaceData);
  if (state.body) {
    state.body = replaceHandleBars(state.body, replaceData);
  }
  const newHeaders: typeof state.headers = [];
  state.headers.forEach(header => {
    const key = replaceHandleBars(header.key, replaceData);
    const value = replaceHandleBars(header.value, replaceData);
    newHeaders.push({ ...header, key, value });
  });
  state.headers = newHeaders;

  const newParams: typeof state.params = [];
  state.params.forEach(param => {
    const key = replaceHandleBars(param.key, replaceData);
    const value = replaceHandleBars(param.value, replaceData);
    newParams.push({ ...param, key, value });
  });
  state.params = newParams;

  return state;
};
